import { useEffect, useState } from "react";
import { useParams } from 'react-router';
import query from "../../../../server/constants.api";
import useFetch from "../../../../server/useFetch";
import { useTranslation } from "react-i18next";
import i18next from 'i18next';
import { BasicTable } from '@synergy/table';
import { Row, Col, Button, Flex, Space, Input, Anchor, Radio, DatePicker, FloatButton, InputNumber, Modal, Divider, notification, ConfigProvider } from 'antd';
import { Text } from '../../Atomics/Text';
import { Widget } from "../../Atomics/Widget";
import { SelectAtomic } from '../../Atomics/SelectAtomic';
import Spinner from "../../Atomics/Spinner";

export const CostTable = ({ dataToFilter, costRef1, costRef2, costRef3, voltage, hasCostSimulation, isCostSimulationLoading, simulationTotalCost }) => {
  const { t } = useTranslation();
  const { id } = useParams();

  const { data: detailSimulationData, isLoading: detailSimulationDataIsLoading, refetch: refetchDetailSimulationData, error: detailSimulationDataError } =
    useFetch(query.path.getRegistryDetailSimulation, 'getRegistryDetailSimulation',
      { id: id, filters: JSON.stringify(dataToFilter) }
    );

  const [fetchedSelectTariffsData, setFetchedSelectTariffsData] = useState(null);
  const [tariffType, setTariffType] = useState('simples');
  const [periodType, setPeriodType] = useState('diario');
  const [disabledPeriodicity, setDisabledPeriodicity] = useState(tariffType === 'tetra-horario' ? false : true);
  const [disableBiHourlyInput, setDisableBiHourlyInput] = useState(true);
  const [disableTriHourlyInput, setDisableTriHourlyInput] = useState(true);
  const [disableTetraHourlyInput, setDisableTetraHourlyInput] = useState(true);
  const [simpleInput, setSimpleInput] = useState(0);
  const [pontaInput, setPontaInput] = useState(0);
  const [cheiaInput, setCheiaInput] = useState(0);
  const [vazioInput, setVazioInput] = useState(0);
  const [superVazioInput, setSuperVazioInput] = useState(0);
  const [indutiveInput, setIndutiveInput] = useState(0);
  const [capacitiveInput, setCapacitiveInput] = useState(0);
  const [fixedTariffInput, setFixedTariffInput] = useState(0);
  const [contractPotencInput, setContractPotencyInput] = useState(0);
  const [pontaHoursInput, setPontaHoursInput] = useState(0);

  const [costData, setCostData] = useState();
  const [costChartData, setChartData] = useState(null);
  const [totalCost, setTotalCost] = useState(0);

  const [costTableLoader, setCostTableLoader] = useState(false);

  useEffect(() => {
    isCostSimulationLoading(detailSimulationDataIsLoading)
  }, [detailSimulationDataIsLoading]);

  useEffect(() => {
    if (!detailSimulationDataIsLoading && detailSimulationData?.data) {
      setFetchedSelectTariffsData(detailSimulationData?.data);
      handleDataChange(detailSimulationData?.data);
      hasCostSimulation(true)
    } else if (!detailSimulationDataIsLoading && detailSimulationDataError) {
      console.error('(Register detail page) Cost simulation: Error in useEffect setting detail simulation data:', detailSimulationDataError);
    }
  }, [detailSimulationData, detailSimulationDataError]);

  useEffect(() => {
    setTariffType(voltage === 'BTN' ? 'simples' : 'tetra-horario');
  }, [voltage]);

  useEffect(() => {
    // Handle data for table and charts after input changes
    handleDataChange(detailSimulationData?.data);
  }, [tariffType, periodType, simpleInput, pontaInput, cheiaInput, vazioInput, superVazioInput, indutiveInput, capacitiveInput, fixedTariffInput, contractPotencInput, pontaHoursInput]);

  const valueCalc = (costData, value) => {
    return (costData?.[tariffType]?.[periodType]?.[value] ?? 0)
  }
  useEffect(() => {
    handleTariffChange();
  }, [voltage]);

  //Handle pre written values on inputs depending on voltage type
  const handleTariffChange = () => {
    setSimpleInput(voltage === 'MT' ? 0 : voltage === 'BTE' ? 0 : 0);
    setPontaInput(voltage === 'MT' ? 0.1391 : voltage === 'BTE' ? 0.1618 : 0);
    setCheiaInput(voltage === 'MT' ? 0.1305 : voltage === 'BTE' ? 0.1504 : 0);
    setVazioInput(voltage === 'MT' ? 0.1109 : voltage === 'BTE' ? 0.1267 : 0);
    setSuperVazioInput(voltage === 'MT' ? 0.0988 : voltage === 'BTE' ? 0.1103 : 0);
    setIndutiveInput(voltage === 'MT' ? 0.0252 : voltage === 'BTE' ? 0.0318 : 0);
    setCapacitiveInput(voltage === 'MT' ? 0.0189 : voltage === 'BTE' ? 0.0243 : 0);
    setFixedTariffInput(voltage === 'MT' ? 0 : voltage === 'BTE' ? 0 : 0);
    setContractPotencyInput(voltage === 'MT' ? 0.034 : voltage === 'BTE' ? 0.045 : 0);
    setPontaHoursInput(voltage === 'MT' ? 0.2258 : voltage === 'BTE' ? 0.4872 : 0);
  }

  // Handle inputs, table and charts after data changed
  const handleDataChange = (costData) => {

    //Enable/disable dropdowns/inputs depending on tariff type selected
    setDisabledPeriodicity(tariffType === 'simples' ? true : false);
    setDisableBiHourlyInput(tariffType === 'simples' ? true : false);
    setDisableTriHourlyInput(tariffType === 'simples' || tariffType === 'bi-horario' ? true : false);
    setDisableTetraHourlyInput(tariffType === 'tetra-horario' ? false : true);

    // Construct array with the structure needed for Table library
    let array = [
      {
        id: 'activeEnergy',
        key: 'activeEnergy',
        tipo: t('activeEnergy') + ' (kWh)',
        quantidade: tariffType === 'simples' ? (costData?.simples?.todo_o_dia ?? 0).toFixed(4)
          : tariffType === 'bi-horario' ? (valueCalc(costData, 'cheias') + valueCalc(costData, 'vazio')).toFixed(4)
            : tariffType === 'tri-horario' ? (valueCalc(costData, 'cheias') + valueCalc(costData, 'vazio') + valueCalc(costData, 'ponta')).toFixed(4)
              : tariffType === 'tetra-horario' ? (valueCalc(costData, 'cheias') + valueCalc(costData, 'vazio') + valueCalc(costData, 'ponta') + valueCalc(costData, 'super_vazio')).toFixed(4)
                : '-',
        custo: tariffType === 'simples' ? (simpleInput * costData?.simples?.todo_o_dia ?? 0).toFixed(2)
          : tariffType === 'bi-horario' ? (cheiaInput * valueCalc(costData, 'cheias') + vazioInput * valueCalc(costData, 'vazio')).toFixed(2)
            : tariffType === 'tri-horario' ? (cheiaInput * valueCalc(costData, 'cheias') + vazioInput * valueCalc(costData, 'vazio') + pontaInput * valueCalc(costData, 'ponta')).toFixed(2)
              : tariffType === 'tetra-horario' ? (cheiaInput * valueCalc(costData, 'cheias') + vazioInput * valueCalc(costData, 'vazio') + pontaInput * valueCalc(costData, 'ponta') + superVazioInput * valueCalc(costData, 'super_vazio')).toFixed(2)
                : 0,
      },
      {
        id: 'simple',
        key: 'simple',
        tipo: t('simple'),
        quantidade: tariffType == 'simples'
          ? costData?.simples?.todo_o_dia.toFixed(4) ?? 0 : '-',
        custo: tariffType == 'simples'
          ? (simpleInput * costData?.simples?.todo_o_dia ?? 0).toFixed(2) : '-',
        hidden: tariffType === 'tetra-horario' ? true : false
      },
      {
        id: 'vazio',
        key: 'vazio',
        tipo: t('empty'),
        quantidade: tariffType !== 'simples' ? valueCalc(costData, 'vazio').toFixed(4) : '-',
        custo: tariffType !== 'simples' ? (vazioInput * valueCalc(costData, 'vazio')).toFixed(2) : '-',
        hidden: false
      },
      {
        id: 'cheia',
        key: 'cheia',
        tipo: t('full'),
        quantidade: tariffType !== 'simples' ? valueCalc(costData, 'cheias').toFixed(4) : '-',
        custo: tariffType !== 'simples' ? (cheiaInput * valueCalc(costData, 'cheias')).toFixed(2) : '-',
        hidden: false
      },
      {
        id: 'ponta',
        key: 'ponta',
        tipo: t('edge'),
        quantidade: tariffType == 'tri-horario' || tariffType == 'tetra-horario' ? valueCalc(costData, 'ponta').toFixed(4) : '-',
        custo: tariffType == 'tri-horario' || tariffType == 'tetra-horario' ? (pontaInput * valueCalc(costData, 'ponta')).toFixed(2) : '-',
        hidden: false
      },
      {
        id: 'vazioSuper',
        key: 'vazioSuper',
        tipo: t('superEmpty'),
        quantidade: tariffType == 'tetra-horario' ? valueCalc(costData, 'super_vazio').toFixed(4) : '-',
        custo: tariffType == 'tetra-horario' ? (superVazioInput * valueCalc(costData, 'super_vazio')).toFixed(2) : '-',
        hidden: tariffType === 'tetra-horario' ? false : true
      },
      {
        id: 'reactiveEnergy',
        key: 'reactiveEnergy',
        tipo: t('reactiveEnergy') + ' (kVArh)',
        quantidade: tariffType === 'tetra-horario' ? (valueCalc(costData, 'indutiva') + valueCalc(costData, 'capacitiva')).toFixed(4) : '-',
        custo: tariffType === 'tetra-horario' ? (indutiveInput * valueCalc(costData, 'indutiva') + capacitiveInput * valueCalc(costData, 'capacitiva')).toFixed(2) : 0,
        hidden: tariffType === 'tetra-horario' ? false : true
      },
      {
        id: 'indutive',
        key: 'indutive',
        tipo: t('inductive'),
        quantidade: tariffType === 'tetra-horario' ? valueCalc(costData, 'indutiva').toFixed(4) : '-',
        custo: tariffType === 'tetra-horario' ? (indutiveInput * valueCalc(costData, 'indutiva')).toFixed(2) : '-',
        hidden: tariffType === 'tetra-horario' ? false : true
      },
      {
        id: 'capacitive',
        key: 'capacitive',
        tipo: t('capacitive'),
        quantidade: tariffType === 'tetra-horario' ? valueCalc(costData, 'capacitiva').toFixed(4) : '-',
        custo: tariffType === 'tetra-horario' ? (capacitiveInput * valueCalc(costData, 'capacitiva')).toFixed(2) : '-',
        hidden: tariffType === 'tetra-horario' ? false : true
      },
      {
        id: 'potency',
        key: 'potency',
        tipo: t('powerChanges'),
        quantidade: '-',
        custo: ((tariffType === 'tetra-horario' ? (pontaHoursInput * valueCalc(costData, 'potencia_horas_de_ponta')) : 0) + (fixedTariffInput * costData?.['termo_fixo'] ?? 0) + (contractPotencInput * costData?.['potencia_contratada'] ?? 0)).toFixed(2),
        hidden: false
      },
      {
        id: 'fixedTariff',
        key: 'fixedTariff',
        tipo: t('fixedTariffTerm') + ' (' + t('days') + ')',
        quantidade: costData?.['termo_fixo'] ?? 0,
        custo: (fixedTariffInput * costData?.['termo_fixo'] ?? 0).toFixed(2),
        hidden: false
      },
      {
        id: 'contractPotency',
        key: 'contractPotency',
        tipo: t('contractedPower') + ' (' + t('days') + ')',
        quantidade: costData?.['potencia_contratada'] ?? 0,
        custo: (contractPotencInput * costData?.['potencia_contratada'] ?? 0).toFixed(2),
        hidden: false
      },
      {
        id: 'pontaHours',
        key: 'pontaHours',
        tipo: t('peakHours') + ' (kW.' + t('days') + ')',
        quantidade: tariffType === 'tetra-horario' ? valueCalc(costData, 'potencia_horas_de_ponta').toFixed(4) : '-',
        custo: tariffType === 'tetra-horario' ? (pontaHoursInput * valueCalc(costData, 'potencia_horas_de_ponta')).toFixed(2) : '-',
        hidden: tariffType === 'tetra-horario' ? false : true
      },
    ]

    //Filter only items to show in array to send to the Table library 
    let newArray = array.filter((item) => !item.hidden)

    setCostData(newArray)

    // Construct array with the structure needed for Chart library
    let chartArray = [
      { name: t('simple'), value: Number((simpleInput * costData?.simples?.todo_o_dia ?? 0).toFixed(2)), hidden: tariffType === 'tetra-horario' ? true : false },
      { name: t('empty'), value: tariffType !== 'simples' ?  Number((vazioInput * valueCalc(costData, 'vazio')).toFixed(2)) : 0.00, hidden: false },
      { name: t('full'), value: tariffType !== 'simples' ? Number((cheiaInput * valueCalc(costData, 'cheias')).toFixed(2)) : 0.00, hidden: false },
      { name: t('edge'), value: tariffType == 'tri-horario' || tariffType == 'tetra-horario' ?  Number((pontaInput * valueCalc(costData, 'ponta')).toFixed(2)) : 0.00, hidden: false },
      { name: t('superEmpty'), value:  Number((superVazioInput * valueCalc(costData, 'super_vazio')).toFixed(2)), hidden: tariffType === 'tetra-horario' ? false : true },
      { name: t('inductiveEnergy'), value:  Number((indutiveInput * valueCalc(costData, 'indutiva')).toFixed(2)), hidden: tariffType === 'tetra-horario' ? false : true },
      { name: t('capacitiveEnergy'), value:  Number((capacitiveInput * valueCalc(costData, 'capacitiva')).toFixed(2)), hidden: tariffType === 'tetra-horario' ? false : true },
      { name: t('fixedTariffTerm'), value: fixedTariffInput && fixedTariffInput > 0 ? Number((fixedTariffInput * costData?.['termo_fixo'] ?? 0).toFixed(2)) : 0.00, hidden: false },
      { name: t('contractedPower'), value:  contractPotencInput && contractPotencInput > 0 ? Number((contractPotencInput * costData?.['potencia_contratada'] ?? 0).toFixed(2)) : 0.00, hidden: false },
      { name: t('peakHours'), value:  Number((pontaHoursInput * valueCalc(costData, 'potencia_horas_de_ponta')).toFixed(2)), hidden: tariffType === 'tetra-horario' ? false : true },
    ]

    //Filter only items to show in array to send to the Table library 
    let newChartArray = chartArray.filter((item) => !item?.hidden)

    setChartData(newChartArray)

    // Logic for Big Number with total cost
    let total =
      tariffType === 'simples' ? simpleInput * costData?.simples?.todo_o_dia + fixedTariffInput * costData?.termo_fixo + contractPotencInput * costData?.potencia_contratada :
        tariffType === 'bi-horario' ? cheiaInput * valueCalc(costData, 'cheias') + vazioInput * valueCalc(costData, 'vazio') + fixedTariffInput * costData?.termo_fixo + contractPotencInput * costData?.potencia_contratada :
          tariffType === 'tri-horario' ? cheiaInput * valueCalc(costData, 'cheias') + vazioInput * valueCalc(costData, 'vazio') + pontaInput * valueCalc(costData, 'ponta') + fixedTariffInput * costData?.termo_fixo + contractPotencInput * costData?.potencia_contratada :
            tariffType === 'tetra-horario' ? cheiaInput * valueCalc(costData, 'cheias') + vazioInput * valueCalc(costData, 'vazio') + pontaInput * valueCalc(costData, 'ponta') + superVazioInput * valueCalc(costData, 'super_vazio') + indutiveInput * valueCalc(costData, 'indutiva') + capacitiveInput * valueCalc(costData, 'capacitiva') + fixedTariffInput * costData?.termo_fixo + contractPotencInput * costData?.potencia_contratada + pontaHoursInput * valueCalc(costData, 'potencia_horas_de_ponta')
              : 0

    setTotalCost((total).toFixed(2))
    simulationTotalCost((total).toFixed(2))

    //Remove loader after chages applied
    setCostTableLoader(false)

  }

  useEffect(() => {
    hasCostSimulation(false)
    refetchDetailSimulationData();
    setCostTableLoader(true);
  }, [dataToFilter]);

  return (
    <Flex id='Cost Estimation' className='ant-anchors-content-item' gap={32} vertical="true">
      <Text $fontColor='coolgray-12' $fontSize='text_xl' $weight='regular' $marginRight='s03'>{t('costEstimation')}</Text>
      <Flex vertical="true">
        <Row gutter={[32, 32]}>
          <Col span={6}>
            <div ref={costRef1}>
              <Flex gap={0} vertical='true'>
                <Divider> <Text color='coolgray-12' $fontSize='text_md'>{t('consumption')}</Text></Divider>
                <Flex gap={16} vertical='true'>
                  <Row gutter={16} align='middle'>
                    <Col span={12}>
                      {tariffType === 'tetra-horario'
                        ? <Text color='coolgray-12' $fontSize='text_md'>{t('fourHourly')}</Text>
                        : <SelectAtomic optionsData={[
                          { value: 'simples', label: t('simple') },
                          { value: 'bi-horario', label: t('biHourly') },
                          { value: 'tri-horario', label: t('triHourly') }
                        ]} defaultValue={tariffType} handleOptionsChange={(value) => { setTariffType(value); handleTariffChange() }} fullWidth={true} />
                      }
                    </Col>
                    <Col span={12}>
                      <SelectAtomic optionsData={[
                        { value: 'diario', label: t('daily') },
                        { value: 'semanal', label: t('weekly') }
                      ]} defaultValue={periodType}
                        isToDisable={disabledPeriodicity} fullWidth={true} handleOptionsChange={(value) => setPeriodType(value)} />
                    </Col>
                  </Row>
                  <Row gutter={[8, 8]}>
                    {voltage == 'BTN' ?
                      <Col span={18}>
                        <Text color='coolgray-12' $fontSize='text_sm'>{t('simple')}</Text>
                      </Col>
                      : null}
                    {voltage == 'BTN' ?
                      <Col span={6}>
                        <InputNumber
                          disabled={tariffType !== 'simples'
                            ? true : false}
                          value={simpleInput}
                          formatter={(value) => `€ ${value}`}
                          parser={(value) => value.replace(/€\s?|(,*)/g, '')}
                          onChange={(e) => setSimpleInput(e)}
                        />
                      </Col>
                      : null}
                    <Col span={18}>
                      <Text color='coolgray-12' $fontSize='text_sm'>{t('empty')}</Text>
                    </Col>
                    <Col span={6}>
                      <InputNumber
                        disabled={disableBiHourlyInput ? true : false}
                        value={vazioInput}
                        formatter={(value) => `€ ${value}`}
                        parser={(value) => value.replace(/€\s?|(,*)/g, '')}
                        onChange={(e) => setVazioInput(e)}
                      />
                    </Col>
                    <Col span={18}>
                      <Text color='coolgray-12' $fontSize='text_sm'>{t('full')}</Text>
                    </Col>
                    <Col span={6}>
                      <InputNumber
                        disabled={disableBiHourlyInput ? true : false}
                        value={cheiaInput}
                        formatter={(value) => `€ ${value}`}
                        parser={(value) => value.replace(/€\s?|(,*)/g, '')}
                        onChange={(e) => setCheiaInput(e)}
                      />
                    </Col>
                    <Col span={18}>
                      <Text color='coolgray-12' $fontSize='text_sm'>{t('edge')}</Text>
                    </Col>
                    <Col span={6}>
                      <InputNumber
                        disabled={disableTriHourlyInput ? true : false}
                        value={pontaInput}
                        formatter={(value) => `€ ${value}`}
                        parser={(value) => value.replace(/€\s?|(,*)/g, '')}
                        onChange={(e) => setPontaInput(e)}
                      />
                    </Col>
                    {voltage == 'BTN' ? null :
                      <Col span={18}>
                        <Text color='coolgray-12' $fontSize='text_sm'>{t('superEmpty')}</Text>
                      </Col>
                    }
                    {voltage == 'BTN' ? null :
                      <Col span={6}>
                        <InputNumber
                          disabled={disableTetraHourlyInput ? true : false}
                          value={superVazioInput}
                          formatter={(value) => `€ ${value}`}
                          parser={(value) => value.replace(/€\s?|(,*)/g, '')}
                          onChange={(e) => setSuperVazioInput(e)}
                        />
                      </Col>

                    }
                  </Row>
                  <Row gutter={[8, 8]} justify="space-between" >
                    {voltage == 'BTN' ? null :
                      <Col span={18}>
                        <Text color='coolgray-12' $fontSize='text_sm'>{t('inductiveEnergy')}</Text>
                        <Text color='coolgray-12' $fontSize='text_xs' $weight='light'>{t('providedByTheGrid')}</Text>
                      </Col>
                    }
                    {voltage == 'BTN' ? null :
                      <Col span={6}>
                        <InputNumber
                          value={indutiveInput}
                          formatter={(value) => `€ ${value}`}
                          parser={(value) => value.replace(/€\s?|(,*)/g, '')}
                          onChange={(e) => setIndutiveInput(e)}
                        />
                      </Col>
                    }
                    {voltage == 'BTN' ? null :
                      <Col span={18}>
                        <Text color='coolgray-12' $fontSize='text_sm'>{t('capacitiveEnergy')}</Text>
                        <Text color='coolgray-12' $fontSize='text_xs' $weight='light'>{t('injectedIntoTheGrip')}</Text>
                      </Col>
                    }
                    {voltage == 'BTN' ? null :
                      <Col span={6}>
                        <InputNumber
                          value={capacitiveInput}
                          formatter={(value) => `€ ${value}`}
                          parser={(value) => value.replace(/€\s?|(,*)/g, '')}
                          onChange={(e) => setCapacitiveInput(e)}
                        />
                      </Col>
                    }
                  </Row>
                </Flex>
                <Divider> <Text color='coolgray-12' $fontSize='text_sm'>{t('powerCharges')}</Text></Divider>
                <Row gutter={[8, 8]} justify="space-between" >
                  <Col span={18}>
                    <Text color='coolgray-12' $fontSize='text_sm'>{t('fixedTariffTerm')}</Text>
                    <Text color='coolgray-12' $fontSize='text_xs' $weight='light'>€/{t('day')}</Text>
                  </Col>
                  <Col span={6}>
                    <InputNumber
                      value={fixedTariffInput}
                      formatter={(value) => `€ ${value}`}
                      parser={(value) => value.replace(/€\s?|(,*)/g, '')}
                      onChange={(e) => setFixedTariffInput(e)}
                    />
                  </Col>
                  <Col span={18}>
                    <Text color='coolgray-12' $fontSize='text_sm'>{t('contractedPower')}</Text>
                    <Text color='coolgray-12' $fontSize='text_xs' $weight='light'>€.kW/{t('day')}</Text>
                  </Col>
                  <Col span={6}>
                    <InputNumber
                      value={contractPotencInput}
                      formatter={(value) => `€ ${value}`}
                      parser={(value) => value.replace(/€\s?|(,*)/g, '')}
                      onChange={(e) => setContractPotencyInput(e)}
                    />
                  </Col>
                  {voltage === 'BTN' ?
                    null
                    :
                    <>
                      <Col span={18}>
                        <Text color='coolgray-12' $fontSize='text_sm'>{t('powerPeakHours')}</Text>
                        <Text color='coolgray-12' $fontSize='text_xs' $weight='light'>€.kW/{t('day')}</Text>
                      </Col>
                      <Col span={6}>
                        <InputNumber
                          value={pontaHoursInput}
                          formatter={(value) => `€ ${value}`}
                          parser={(value) => value.replace(/€\s?|(,*)/g, '')}
                          onChange={(e) => setPontaHoursInput(e)}
                        />
                      </Col>
                    </>

                  }
                </Row>
              </Flex>
            </div>

          </Col>
          {costTableLoader
            ? <Col span={18}>
              <Flex className="full-h" align="center" justify='center' horizontal="true" ref={costRef2}>
                <Spinner stroke="green" size='52px' />
              </Flex>
            </Col>
            : <Col span={8} className='compact-table tariff-simulation-table'>
              <div ref={costRef2}>
                <BasicTable tableId='tariffsSimulation' fetchData={costData} hidePagination={true} language={i18next.resolvedLanguage} />
              </div>
            </Col>
          }
          {costTableLoader ? null :
            <Col span={10}>
              <div ref={costRef3}>
                <Flex gap={16} vertical="true">
                  <Widget data={{
                    height: '400px',
                    background: 'white',
                    charts: [
                      {
                        chartType: 'horizontalBarChartApex',
                        chartId: 'costsEstimation',
                        data: {
                          series: costChartData ?? null,
                          categories: null,
                          xAxisType: null,
                          labelFormat: null,
                          tooltip: null,
                          markerSize: null,
                          hideCSVIcon: true,
                          unit: "€",
                          columnWidth: 20,
                          xAxisLabel: "€",
                          color: null,
                        }
                      }
                    ]
                  }} municipalitiesToFetch="Porto" />
                  <Widget data={{
                    height: '130px',
                    background: 'white',
                    title: t('finalCost'),
                    icon: 'Euro',
                    charts: [{
                      chartType: 'bigNumberChart',
                      chartId: 'bigNumberChartOne',
                      data: {
                        number: totalCost,
                        unit: null,
                        type: null,
                        label: null,
                        fluctuation: null,
                        fluctuationText: null,
                        bigFluctuation: false,
                        icon: 'Euro'
                      }
                    }]
                  }} />
                </Flex>
              </div>

            </Col>
          }
        </Row>
      </Flex>
    </Flex>
  )
}